import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OmpLinkConnectResult, OmpTestResult } from '@cloudsign/shared';
import type { OmpLinkStatus, OmpMessage } from '@cloudsign/shared';
import { Alert, Button, Card, ConfirmButton, EmptyState, LinkButton, PageHeader, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { SecretDialog, StatusPill, isValidEmail } from '../../components/phase2';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { MessageList, OmpLinkStatus as StatusSchema, RotateResult, SiteLinkList, ompKey, useGroups, useModuleOn } from './api';

type Reveal = { title: string; description: string; label: string; secret: string };

/** Integrations: the optional two-way link (spec 9.1 rule 7). Shows nothing about the link until the module is switched on. */
export function OmpLinkPage() {
  const on = useModuleOn('omp-link');
  if (!on) {
    return (
      <div>
        <PageHeader title={t('peo.omp.off.title')} />
        <EmptyState message={t('peo.omp.off.body')} />
      </div>
    );
  }
  return <OmpLinkContent />;
}

function OmpLinkContent() {
  const status = useQuery({ queryKey: [...ompKey, 'status'], queryFn: () => api.get('/omp-link/status', { schema: StatusSchema }) });
  const [reveals, setReveals] = useState<Reveal[]>([]);
  const reveal = (r: Reveal[]) => setReveals((cur) => [...cur, ...r]);
  return (
    <div className="space-y-6">
      <PageHeader title={t('peo.omp.title')} description={t('peo.omp.description')} />
      {status.isError ? (
        <ErrorPanel error={status.error} onRetry={() => void status.refetch()} />
      ) : !status.data ? (
        <Spinner />
      ) : status.data.configured ? (
        <Connected status={status.data} onReveal={reveal} />
      ) : (
        <ConnectForm onReveal={reveal} />
      )}
      {reveals[0] ? (
        <SecretDialog
          key={reveals[0].label}
          title={reveals[0].title}
          description={reveals[0].description}
          secret={reveals[0].secret}
          secretLabel={reveals[0].label}
          onClose={() => setReveals((cur) => cur.slice(1))}
        />
      ) : null}
    </div>
  );
}

function ConnectForm({ onReveal }: { onReveal: (r: Reveal[]) => void }) {
  const canManage = useCan('omp_link.manage');
  const qc = useQueryClient();
  const groups = useGroups();
  const [webhookUrl, setWebhookUrl] = useState('');
  const [alertEmail, setAlertEmail] = useState('');
  const [groupId, setGroupId] = useState('');
  const repeat = (groups.data ?? []).filter((g) => g.kind === 'repeat');
  const gid = groupId || repeat[0]?.id || '';
  const connect = useMutation({
    mutationFn: () => api.post('/omp-link/connect', { body: { webhookUrl: webhookUrl.trim(), alertEmail: alertEmail.trim() === '' ? null : alertEmail.trim(), defaultGroupId: gid }, schema: OmpLinkConnectResult }),
    onSuccess: (r) => {
      onReveal([
        { title: t('peo.omp.secret.keyTitle'), description: t('peo.omp.secret.description'), label: t('peo.omp.secret.keyLabel'), secret: r.apiKey },
        { title: t('peo.omp.secret.webhookTitle'), description: t('peo.omp.secret.description'), label: t('peo.omp.secret.webhookLabel'), secret: r.webhookSecret },
      ]);
      void qc.invalidateQueries({ queryKey: ompKey });
      // Connecting creates (and disconnecting removes) a managed API client and webhook endpoint.
      void qc.invalidateQueries({ queryKey: ['api-clients'] });
      void qc.invalidateQueries({ queryKey: ['webhooks'] });
    },
  });
  const errs = fieldErrors(connect.error);
  const urlBad = webhookUrl.trim() !== '' && !/^https?:\/\/\S+$/i.test(webhookUrl.trim());
  const mailBad = alertEmail.trim() !== '' && !isValidEmail(alertEmail.trim());
  const valid = webhookUrl.trim() !== '' && !urlBad && !mailBad && gid !== '';
  return (
    <Card
      title={t('peo.omp.connect.title')}
      description={t('peo.omp.connect.description')}
      footer={
        canManage ? (
          <Button variant="primary" disabled={!valid || connect.isPending} onClick={() => connect.mutate()}>
            {t('peo.omp.connect.submit')}
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-4">
        <TextField label={t('peo.omp.connect.url')} hint={t('peo.omp.connect.urlHelp')} type="url" value={webhookUrl} disabled={!canManage} onChange={(e) => setWebhookUrl(e.target.value)} error={urlBad ? t('peo.omp.connect.urlBad') : errs['webhookUrl']} />
        <TextField label={t('peo.omp.connect.email')} hint={t('peo.omp.connect.emailHelp')} type="email" value={alertEmail} disabled={!canManage} onChange={(e) => setAlertEmail(e.target.value)} error={mailBad ? t('peo.omp.connect.emailBad') : errs['alertEmail']} />
        <SelectField label={t('peo.omp.connect.group')} hint={t('peo.omp.connect.groupHelp')} value={gid} disabled={!canManage} error={errs['defaultGroupId']} onChange={(e) => setGroupId(e.target.value)}>
          {repeat.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </SelectField>
        <ErrorLine error={connect.error} />
      </div>
    </Card>
  );
}

function Connected({ status, onReveal }: { status: OmpLinkStatus; onReveal: (r: Reveal[]) => void }) {
  const canManage = useCan('omp_link.manage');
  const qc = useQueryClient();
  const toast = useToast();
  const [test, setTest] = useState<OmpTestResult | null>(null);
  const testNow = useMutation({
    mutationFn: () => api.post('/omp-link/test', { schema: OmpTestResult }),
    onSuccess: setTest,
  });
  const rotate = useMutation({
    mutationFn: (what: 'api_key' | 'webhook_secret') => api.post('/omp-link/rotate', { body: { what }, schema: RotateResult }).then((r) => ({ what, secret: r.secret })),
    onSuccess: ({ what, secret }) =>
      onReveal([
        what === 'api_key'
          ? { title: t('peo.omp.secret.keyTitle'), description: t('peo.omp.secret.description'), label: t('peo.omp.secret.keyLabel'), secret }
          : { title: t('peo.omp.secret.webhookTitle'), description: t('peo.omp.secret.description'), label: t('peo.omp.secret.webhookLabel'), secret },
      ]),
  });
  const disconnect = useMutation({
    mutationFn: () => api.post('/omp-link/disconnect', { schema: StatusSchema }),
    onSuccess: () => {
      toast(t('peo.omp.disconnected'));
      void qc.invalidateQueries({ queryKey: ompKey });
      // Connecting creates (and disconnecting removes) a managed API client and webhook endpoint.
      void qc.invalidateQueries({ queryKey: ['api-clients'] });
      void qc.invalidateQueries({ queryKey: ['webhooks'] });
    },
  });
  const tone = status.webhookStatus === 'failing' ? 'bad' : status.webhookStatus === 'paused' ? 'warn' : 'good';
  const stat = (label: string, value: string | number) => (
    <div>
      <dt className="text-xs text-slate-600">{label}</dt>
      <dd className="text-sm font-medium text-slate-900">{value}</dd>
    </div>
  );
  return (
    <>
      <Card
        title={t('peo.omp.status.title')}
        footer={
          canManage ? (
            <>
              <Button disabled={testNow.isPending} onClick={() => testNow.mutate()}>
                {t('peo.omp.test')}
              </Button>
              <ConfirmButton label={t('peo.omp.rotateKey')} confirmLabel={t('peo.omp.rotateConfirm')} pending={rotate.isPending} onConfirm={() => rotate.mutate('api_key')} />
              <ConfirmButton label={t('peo.omp.rotateSecret')} confirmLabel={t('peo.omp.rotateConfirm')} pending={rotate.isPending} onConfirm={() => rotate.mutate('webhook_secret')} />
              <ConfirmButton label={t('peo.omp.disconnect')} confirmLabel={t('peo.omp.disconnectConfirm')} pending={disconnect.isPending} onConfirm={() => disconnect.mutate()} />
            </>
          ) : undefined
        }
      >
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill tone={tone}>{t(`peo.omp.webhook.${status.webhookStatus ?? 'active'}`)}</StatusPill>
            {status.failingSince ? <span className="text-sm text-slate-700">{t('peo.omp.failingSince', { when: formatDateTime(status.failingSince) })}</span> : null}
          </div>
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {stat(t('peo.omp.status.lastOut'), status.lastOutboundAt ? formatDateTime(status.lastOutboundAt) : t('peo.omp.never'))}
            {stat(t('peo.omp.status.lastIn'), status.lastInboundAt ? formatDateTime(status.lastInboundAt) : t('peo.omp.never'))}
            {stat(t('peo.omp.status.key'), status.apiKeyId ?? '-')}
            {stat(t('peo.omp.status.sites'), status.linkedSites)}
            {stat(t('peo.omp.status.people'), status.linkedPeople)}
            {stat(t('peo.omp.status.unmatched'), status.unmatchedPeople)}
          </dl>
          {status.unmatchedPeople > 0 ? (
            <LinkButton to="/manage/api-clients?tab=matching&system=omp" className="w-fit">
              {t('peo.omp.matchLink', { count: status.unmatchedPeople })}
            </LinkButton>
          ) : null}
          {test ? <Alert kind={test.ok ? 'success' : 'error'}>{test.ok ? t('peo.omp.test.ok', { ms: test.durationMs }) : test.errorKey ? t(test.errorKey) : t('peo.omp.test.failed', { code: test.statusCode ?? '-' })}</Alert> : null}
          <ErrorLine error={testNow.error ?? rotate.error ?? disconnect.error} />
        </div>
      </Card>
      <SiteLinks />
      <Messages />
    </>
  );
}

function SiteLinks() {
  const canManage = useCan('omp_link.manage');
  const qc = useQueryClient();
  const q = useQuery({ queryKey: [...ompKey, 'sites'], queryFn: () => api.get('/omp-link/sites', { schema: SiteLinkList }) });
  const [ids, setIds] = useState<Record<string, string>>({});
  useEffect(() => {
    if (q.data) setIds(Object.fromEntries(q.data.map((s) => [s.siteId, s.ompOfficeId ?? ''])));
  }, [q.data]);
  const save = useMutation({
    mutationFn: () => api.put('/omp-link/sites', { body: { links: Object.entries(ids).map(([siteId, v]) => ({ siteId, ompOfficeId: v.trim() === '' ? null : v.trim() })) }, schema: SiteLinkList }),
    onSuccess: (r) => {
      qc.setQueryData([...ompKey, 'sites'], r);
      void qc.invalidateQueries({ queryKey: [...ompKey, 'status'] });
    },
  });
  const changed = (q.data ?? []).some((s) => (ids[s.siteId] ?? '').trim() !== (s.ompOfficeId ?? ''));
  return (
    <Card
      title={t('peo.omp.sites.title')}
      description={t('peo.omp.sites.description')}
      footer={
        canManage ? (
          <Button variant="primary" disabled={!changed || save.isPending} onClick={() => save.mutate()}>
            {t('common.save')}
          </Button>
        ) : undefined
      }
    >
      {q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <Spinner />
      ) : (
        <TableWrap caption={t('peo.omp.sites.title')}>
          <thead>
            <tr>
              <Th>{t('peo.omp.sites.site')}</Th>
              <Th>{t('peo.omp.sites.office')}</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {q.data.map((s) => (
              <tr key={s.siteId}>
                <td className="px-3 py-2 align-top text-sm">
                  <span className="font-medium">{s.siteName}</span>
                  <span className="block font-mono text-xs text-slate-600">{s.siteId}</span>
                </td>
                <td className="px-3 py-2">
                  <TextField label={<span className="sr-only">{t('peo.omp.sites.officeFor', { name: s.siteName })}</span>} value={ids[s.siteId] ?? ''} maxLength={100} disabled={!canManage} onChange={(e) => setIds({ ...ids, [s.siteId]: e.target.value })} />
                </td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
      <ErrorLine error={save.error} />
    </Card>
  );
}

/** Outbound lines carry a webhook event name and delivery status; inbound lines carry the call kind and how it was handled. */
function messageType(m: OmpMessage): string {
  return m.direction === 'outbound' ? t(`whk.event.${m.type}`) : t(`peo.omp.msgType.${m.type}`);
}

function messageStatus(m: OmpMessage): string {
  return m.direction === 'outbound' ? t(`whk.delivery.${m.status}`) : t(`peo.omp.msgStatus.${m.status}`);
}

function Messages() {
  const q = useQuery({ queryKey: [...ompKey, 'messages'], queryFn: () => api.get('/omp-link/messages', { query: { limit: 100 }, schema: MessageList }) });
  return (
    <Card title={t('peo.omp.messages.title')} description={t('peo.omp.messages.description')}>
      {q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <Spinner />
      ) : q.data.length === 0 ? (
        <EmptyState message={t('peo.omp.messages.empty')} />
      ) : (
        <TableWrap caption={t('peo.omp.messages.title')}>
          <thead>
            <tr>
              <Th>{t('peo.omp.messages.when')}</Th>
              <Th>{t('peo.omp.messages.direction')}</Th>
              <Th>{t('peo.omp.messages.what')}</Th>
              <Th>{t('common.status')}</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {q.data.map((m, i) => (
              <tr key={`${m.at}-${i}`}>
                <td className="whitespace-nowrap px-3 py-2 text-sm">{formatDateTime(m.at)}</td>
                <td className="px-3 py-2 text-sm">{t(`peo.omp.direction.${m.direction}`)}</td>
                <td className="px-3 py-2 text-sm">
                  {[messageType(m), m.personName, m.siteName].filter(Boolean).join(' · ')}
                  {m.detailKey ? <span className="block text-xs text-slate-600">{t(m.detailKey)}</span> : null}
                </td>
                <td className="px-3 py-2 text-sm">{messageStatus(m)}</td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </Card>
  );
}
